import dayjs from 'dayjs'

/**
 * Formats a numeric price as a currency string (e.g. 249 → "$249", 45.99 → "$45.99").
 * Omits decimals for whole-dollar amounts.
 */
export function formatCurrency(value: number): string {
  return value % 1 === 0 ? `$${value}` : `$${value.toFixed(2)}`
}

/**
 * Formats a numeric price as a currency string with two decimal places always
 * present (e.g. 249 → "$249.00", 45.9 → "$45.90"). Use for receipt/order
 * breakdowns where every line aligns to the same precision.
 */
export function formatCurrencyPrecise(value: number): string {
  return `$${value.toFixed(2)}`
}

/**
 * Formats a price delta as a signed currency string (e.g. 100 → "+$100").
 */
export function formatPriceDelta(delta: number): string {
  return `+${formatCurrency(delta)}`
}

/**
 * Formats a price with a "total" suffix (e.g. 299 → "$299 total").
 */
export function formatPriceTotal(price: number): string {
  return `${formatCurrency(price)} total`
}

/**
 * Formats a 24-hour time string (`"HH:mm"` or `"HH:mm:ss"`) as `h:mm am`/`h:mm pm`.
 * Returns an empty string for empty input so UI bindings stay blank.
 */
export function formatTimeOfDay(time: string): string {
  if (!time) return ''
  const parsed = dayjs(`1970-01-01T${time}`)
  if (!parsed.isValid()) return ''
  return parsed.format('h:mm a')
}

export interface CalendarEvent {
  start: Date
  end: Date
  summary: string
  description?: string
}

export function downloadIcsFile(event: CalendarEvent): void {
  const format = (d: Date) =>
    d
      .toISOString()
      .replace(/[-:]/g, '')
      .replace(/\.\d{3}/, '')

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//CADY Studios//Booking//EN',
    'BEGIN:VEVENT',
    `DTSTART:${format(event.start)}`,
    `DTEND:${format(event.end)}`,
    `SUMMARY:${event.summary}`,
    event.description ? `DESCRIPTION:${event.description}` : '',
    'END:VEVENT',
    'END:VCALENDAR'
  ]
    .filter(Boolean)
    .join('\r\n')

  const blob = new Blob([lines], { type: 'text/calendar;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'event.ics'
  a.click()
  URL.revokeObjectURL(url)
}
